import { create } from "zustand";
import { immer } from "zustand/middleware/immer";
import { treeListService } from "../services/treeListService";
import { treeService } from "../services/treeService";
import { TreeListItem } from "../types/types";

interface TreeListState {
  // List of all available trees
  trees: TreeListItem[];
  // Loading state
  loading: boolean;
  // Error message
  error: string | null;

  // Actions
  fetchTrees: (forceRefresh?: boolean) => Promise<void>;
  deleteTree: (id: string) => Promise<void>;
  refreshCache: () => Promise<void>;
}

/**
 * Store for managing the list of available trees
 *
 * This store is separate from the TreeStore which focuses on
 * the single tree currently being edited
 */
export const useTreeListStore = create<TreeListState>()(
  immer((set) => ({
    trees: [],
    loading: false,
    error: null,

    fetchTrees: async (forceRefresh = false) => {
      set({ loading: true, error: null });
      try {
        const trees = await treeListService.fetchTreeList(forceRefresh);
        console.log("Fetched trees:", trees);
        set({ trees: trees, loading: false });
      } catch (error: any) {
        console.error("Failed to load trees:", error);
        set({
          error: `Failed to load trees: ${error.message || error}`,
          loading: false,
        });
      }
    },

    deleteTree: async (id: string) => {
      set({ loading: true, error: null });
      try {
        await treeService.deleteTree(id);

        // Update the tree list by removing the deleted tree
        set((state) => {
          state.trees = state.trees.filter((tree) => tree.id !== id);
          state.loading = false;
        });

        // Also clear the tree list cache to force a refresh on next fetch
        await treeListService.clearCache();
      } catch (error: any) {
        console.error("Failed to delete tree:", error);
        set({
          error: `Failed to delete tree: ${error.message || error}`,
          loading: false,
        });
      }
    },

    refreshCache: async () => {
      set({ loading: true, error: null });
      try {
        const trees = await treeListService.refreshCache();
        set({ trees, loading: false });
      } catch (error: any) {
        console.error("Failed to refresh tree list:", error);
        set({
          error: `Failed to refresh tree list: ${error.message || error}`,
          loading: false,
        });
      }
    },
  }))
);
